import { useCallback, useEffect, useState } from 'react'
import { get, post, patch } from '../lib/api'
import VoiceOfGod from './VoiceOfGod'
import Tickets from './Tickets'
import Sheets from './Sheets'
import Icon from './Icon'
import { t } from '../lib/i18n'
import Parties from './Parties'
import { Collapsible, Pager, usePaged, HideDone, useHideDone } from './ui'

/**
 * The Vieques closing Command Center — what the owner sees on sign-in.
 *
 * A mirror of the workbook, not a second opinion on it. Every number here was
 * computed by the spreadsheet and imported; the only things this screen works
 * out for itself are the ones that depend on today, and the API does those.
 *
 * The ordering is deliberate and is the workbook's own: the blocker first,
 * because there is exactly one thing to do next; then the countdown, because
 * the deadline is the constraint everything else is measured against; then the
 * financing gap, because it is the risk that decides whether any of the rest
 * matters.
 */

type Metric = {
  key: string; label: string; value_num: number | null; value_text: string | null
  format: string; section: string
  /** True when this app counted it rather than reading it from the workbook. */
  computedHere?: boolean
  /** What the workbook last calculated, when it disagrees with the live count. */
  workbookValue?: number | null
}
type Overridden = Record<string, string | null>
type Gate = { ref: string; name: string; due_on: string | null; pass_condition: string | null; owner: string | null; status: string | null; if_not_passed: string | null; daysUntil: number | null; overdue: boolean; overridden?: Overridden }
type Task = {
  ref: string; workstream: string | null; task: string; owner: string | null
  due_on: string | null; status: string | null; next_action: string | null
  risk_if_late: string | null; overdue: boolean; daysUntil: number | null
  // Both were already served; the type simply never named them, so the detail
  // row could not show the two fields most worth opening a row for.
  dependency: string | null; evidence: string | null
  overridden?: Overridden
}
type Check = { ref: string; name: string; status: string; where_to_fix: string | null; note: string | null; overridden?: Overridden }
type Risk = { ref: string; risk: string; impact: string | null; likelihood: string | null; mitigation: string | null; owner: string | null; status: string | null }

type Attention = {
  kind: 'gate' | 'task'; ref: string; label: string; daysUntil: number | null
  due: string | null; detail: string | null; consequence: string | null; severity: string
}
type Credit = {
  available: boolean; asOf: string | null; fetchedAt: number; ageDays: number
  latest: Array<{ bureau: string; model: string; source: string; score: number; pulledOn: string; isMortgageModel: boolean }>
  mortgageMiddle: { score: number | null; reason?: string }
  disclaimer?: string
}
type Util = {
  lines: Array<{ creditor: string; last4: string | null; balance: number; limit: number; utilisation: number | null }>
  balance: number; limit: number; ratio: number; bureau: string; pulledOn: string
  toThirty: number; toTen: number
  bureaus: Array<{ bureau: string; pulledOn: string; cards: number; balance: number; limit: number; ratio: number | null }>
}
/*
 * The API has always returned more of this than the type admitted. `payoff`,
 * `landNeed`, `maxAt75` and the three `cards*` fields are built in
 * services/api/src/deal.ts and shipped on every response; they were simply
 * never declared, so a consumer reaching for them failed to compile and the
 * data sat unreachable. Declared now — nothing about the payload changed.
 */
type Refi = {
  collateral: number
  owed: number
  sought: number
  ltv: number
  headroomAt75: number
  landNeed: number | null
  /** Card debt the refinance has to clear. Pulled from 42payments, CARDS ONLY. */
  payoff: number | null
  maxAt75: number
  soughtSetByHand?: boolean
  computedSought?: number | null
  /** When 42payments last refreshed the card figure. */
  cardsAsOf?: string | null
  cardsPersonal?: number | null
  cardsBusiness?: number | null
  /** What `payoff` actually counted. A total and a floor must not look alike. */
  debtScope?: 'all' | 'floor' | 'cards' | 'typed'
  debtReviewedOn?: string | null
  cardsOnlyTotal?: number | null
  nonCardTotal?: number | null
  offBureauTotal?: number | null
}
/*
 * The owner's hand-kept checklist, served by /api/admin/checklists.
 *
 * Deliberately not the workbook's task rows: these survive a re-import and
 * carry what the spreadsheet never had — what an item means, and how to do it.
 * It is the list the owner works from, so it belongs on the front page rather
 * than three clicks into Admin.
 */
type ChecklistItem = { id: string; list: string; title: string; detail: string | null; how: string | null; done: boolean }
type ChecklistGroup = { list: string; items: ChecklistItem[] }

type Deal = {
  imported: boolean
  refi: Refi | null
  utilisation: Util | null
  attention: Attention[]
  credit: Credit | null
  sheets: Array<{ sheet: string; title: string | null; rows: number }>
  snapshot: { id: string; createdAt: number; sourcePath: string; sourceModifiedAt: number | null; stale: boolean } | null
  daysToClose: number | null
  closeDate: string | null
  metrics: Metric[]
  statements: Record<string, string>
  gates: Gate[]
  tasks: Task[]
  checks: Check[]
  risks: Risk[]
  counts: { overdue: number; openTasks: number; gatesOpen: number; checksFailing: number; risksOpenHigh: number }
}

const money = (n: number | null) =>
  n === null ? '—' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
const percent = (n: number | null) => (n === null ? '—' : `${(n * 100).toFixed(1)}%`)
/*
 * 2-Sep, not "Sep 2, 2026".
 *
 * Every date on this screen is inside the next two months, so the year is four
 * characters of noise in every row, and the long form pushed the columns that
 * matter off the side of a phone.
 */
const day = (iso: string | null) => {
  if (!iso) return '—'
  const d = new Date(`${iso}T00:00:00Z`)
  const mon = d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })
  return `${d.getUTCDate()}-${mon}`
}

function show(m: Metric | undefined) {
  if (!m) return '—'
  if (m.value_text !== null) return m.format === 'date' ? day(m.value_text) : m.value_text
  if (m.format === 'currency') return money(m.value_num)
  if (m.format === 'percent') return percent(m.value_num)
  if (m.value_num === null) return '—'
  return m.value_num.toLocaleString('en-US')
}

export default function CommandCenter({ environment }: { environment?: string }) {
  const [deal, setDeal] = useState<Deal | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<'path' | 'gates' | 'checks' | 'risks' | 'tickets' | 'sheets' | 'parties'>('path')
  /* The four top-level tabs. `tab` above is the work surface inside The file. */
  const [top, setTop] = useState<'today' | 'funding' | 'debt' | 'file'>('today')
  const [lists, setLists] = useState<ChecklistGroup[]>([])
  const [pulling, setPulling] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  // Rows opened inline. "Details" used to post a command and toast its reply,
  // and the reply for a question is just the row id — so it read as a dead
  // button. The row itself is the answer.
  const [openRows, setOpenRows] = useState<Set<string>>(new Set())
  // Just the count, so the tab can say whether there is anything behind it.
  const [partyCount, setPartyCount] = useState<number | null>(null)
  /*
   * Finished work disappears by default.
   *
   * A closed task, a passed gate, a closed risk and a passing check are all
   * things you have already dealt with. Leaving them in the table is how the
   * four rows that still need you end up below the fold.
   */
  const [hideDoneTasks, setHideDoneTasks] = useHideDone('tasks')
  const [hidePassChecks, setHidePassChecks] = useHideDone('checks')
  const [hideClosedRisks, setHideClosedRisks] = useHideDone('risks')

  const load = useCallback(async () => {
    try {
      setDeal(await get<Deal>('/api/deal'))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the deal.')
    }
  }, [])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    void get<{ contacts: unknown[] }>('/api/parties')
      .then((r) => setPartyCount(r.contacts.length))
      .catch(() => setPartyCount(null))
  }, [])

  /*
   * Fails quietly to an empty list. The checklist migration can legitimately
   * land after the deploy that reads it, and a front page that 500s because a
   * secondary list is not there yet is worse than one without lanes.
   */
  const loadLists = useCallback(async () => {
    try {
      setLists((await get<{ lists: ChecklistGroup[] }>('/api/admin/checklists')).lists)
    } catch {
      setLists([])
    }
  }, [])
  useEffect(() => { void loadLists() }, [loadLists])

  /* Optimistic: the box ticks now and reconciles on the reload. */
  async function toggleProof(item: ChecklistItem) {
    setLists((prev) => prev.map((g) => ({
      ...g,
      items: g.items.map((i) => (i.id === item.id ? { ...i, done: !i.done } : i)),
    })))
    try {
      await patch(`/api/admin/checklists/${item.id}`, { done: !item.done })
    } finally {
      await loadLists()
    }
  }

  /*
   * Run a command from a row.
   *
   * NEEDS INPUT used to print the command you COULD type, which is a caption,
   * not a control. These do the thing.
   */
  /*
   * Run a command from a row, and SAY what happened.
   *
   * This posted and reloaded silently, so "look at it" looked like a dead
   * link — the answer went nowhere. Every row action now leaves a line on
   * screen, the same as typing the command would.
   */
  async function act(command: string) {
    try {
      const reply = await post<{ message?: string }>('/api/voice', { command })
      setToast(reply.message ?? 'Done.')
      window.setTimeout(() => setToast(null), 6000)
      await load()
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'That did not work.')
    }
  }

  /*
   * Refresh everything 42payments owns, not only the score.
   *
   * This pulled the credit profile alone, so the debt figure had no manual
   * trigger at all — it moved only on the nightly job, and before that job
   * existed it never moved. One button, both facts, because "pull from
   * 42payments" is one idea to the person pressing it.
   *
   * Sequential rather than parallel on purpose: if the debt pull fails, the
   * credit pull has already landed and is not rolled back by the error.
   */
  async function pullCredit() {
    setPulling(true)
    try {
      await post('/api/pull/credit')
      await post('/api/pull/debt')
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The pull from 42payments failed.')
    } finally {
      setPulling(false)
    }
  }

  /*
   * Derived above the early returns, because these call hooks.
   *
   * usePaged holds state, so it cannot sit after `if (!deal) return` — React
   * counts hooks per render and a loading render would call fewer of them.
   * Hence the `?? []`: the lists are empty until the deal arrives.
   */
  const done = (v: string | null | undefined) =>
    ['complete', 'passed', 'closed'].includes((v ?? '').trim().toLowerCase())
  const visibleTasks = (deal?.tasks ?? []).filter((t) => !hideDoneTasks || !done(t.status))
  const visibleChecks = (deal?.checks ?? []).filter((c) => !hidePassChecks || c.status !== 'PASS')
  const visibleRisks = (deal?.risks ?? []).filter((r) => !hideClosedRisks || !done(r.status))
  const taskPage = usePaged(visibleTasks)
  const checkPage = usePaged(visibleChecks)
  const riskPage = usePaged(visibleRisks)

  if (error) return <section className="card"><p className="error" role="alert">{error}</p></section>
  if (!deal) return <section className="card"><p className="muted">Loading the model…</p></section>

  if (!deal.imported) {
    /*
     * The command names the environment you are LOOKING AT.
     *
     * It used to say `--env production`, hardcoded, under a sentence that read
     * "this environment". This screen only appears on an environment nobody has
     * imported yet — in practice beta — so it told you that beta was empty and
     * then handed you the command that fills PRODUCTION. The script takes the
     * flag at face value and the write would have landed on the live database.
     *
     * When health has not answered, print a placeholder rather than a guess: an
     * unrunnable command costs a moment, and the wrong runnable one costs the
     * production snapshot.
     */
    const target =
      environment === 'local' ? '--local'
      : environment ? `--env ${environment}`
      : '--env <environment>'
    return (
      <section className="card">
        <h1>No snapshot yet</h1>
        <p className="muted">
          The workbook has not been imported into this environment. Run it from the repo:
        </p>
        <pre><code>node scripts/import-workbook.mjs {target}</code></pre>
      </section>
    )
  }

  const m = (key: string) => deal.metrics.find((x) => x.key === key)
  const control = deal.metrics.filter((x) => x.section === 'control')
  const blocker = deal.statements.current_blocker?.replace(/^CURRENT BLOCKER\s*—\s*/, '') ?? null
  const days = deal.daysToClose

  /*
   * Which row the blocker is about.
   *
   * The workbook's blocker is prose and names no id, so it floated free of the
   * list beneath it. Word overlap is rough, but naming a probable row is far
   * more use than naming none — and it says "most likely" rather than
   * pretending to know.
   */
  const blockerTask = (() => {
    if (!blocker) return null
    const words = new Set(blocker.toLowerCase().match(/[a-z]{5,}/g) ?? [])
    let best: (typeof deal.tasks)[number] | null = null
    let bestScore = 0
    for (const t of deal.tasks) {
      const hay = `${t.task} ${t.next_action ?? ''} ${t.evidence ?? ''}`.toLowerCase()
      let score = 0
      for (const w of words) if (hay.includes(w)) score++
      if (score > bestScore) { bestScore = score; best = t }
    }
    return bestScore >= 2 ? best : null
  })()

  /*
   * The middle of the latest score per bureau.
   *
   * A mortgage is priced on the MIDDLE of three, which is why the middle is the
   * number shown rather than the best or the average. These are FICO 8 and the
   * label says so: 42payments is explicit that a free score is a trend
   * instrument, and the tile must not quietly imply otherwise.
   */
  const scores = (deal.credit?.latest ?? []).map((l) => l.score).sort((a, b) => a - b)
  const creditMid = scores.length === 3 ? scores[1]! : scores.length ? scores[Math.floor(scores.length / 2)]! : null
  const creditLow = scores[0] ?? null
  const creditHigh = scores[scores.length - 1] ?? null
  const workbookScore = m('alert_score')?.value_num ?? null



  /*
   * ── The four tabs ────────────────────────────────────────────────────────
   *
   * Today answers "what do I do now". Funding answers "does the money work".
   * Debt plan answers "where does the next payment go". The file is the
   * record — every work item, every party, and the provenance of each number.
   *
   * The old screen was one column of six equal panels, which said everything
   * was equally urgent. It never was.
   */
  const conflicts = deal.checks.filter((c) => c.status !== 'PASS')
  const locks = deal.gates.filter((g) => !done(g.status))
  const laneItems = lists.filter((l) => l.items.length)
  const proofs = laneItems.flatMap((l) => l.items)
  const proofsDone = proofs.filter((i) => i.done).length

  /* The nearest real deadline, and the contractual one. Both, because the
     lender's clear-to-close is the date work has to land by and the contract
     date is the one that costs money. */
  const ctc = m('operational_close')?.value_text ?? null
  const ctcDays = ctc ? Math.round((Date.parse(`${ctc}T00:00:00Z`) - Date.now()) / 86400000) : null

  const pct = (n: number | null) => (n === null ? null : Math.max(0, Math.min(100, n * 100)))

  return (
    <div className="rg-shell">
      {toast && (
        <div className="cc-toast" role="status" onClick={() => setToast(null)}>{toast}</div>
      )}

      <section className="rg-hero">
        <div>
          <p className="eyebrow" style={{ color: 'var(--brand)' }}>
            Vieques, Puerto Rico · active transaction
          </p>
          <h1>
            {deal.statements.headline
              ?? deal.statements.mission
              ?? 'Buy The Ridge without sacrificing the cash that makes the plan survivable.'}
          </h1>
        </div>
        <div className="rg-dates">
          {/*
            * One button, both facts. "Pull from 42payments" is one idea to the
            * person pressing it, and without it every figure on this page is
            * whatever the nightly job last wrote.
            */}
          <button type="button" className="rg-date rg-pull" onClick={() => void pullCredit()} disabled={pulling}>
            <span className="eyebrow" style={{ color: 'var(--muted)' }}>42payments</span>
            <b>{pulling ? t('cc.pulling') : t('cc.pull')}</b>
          </button>
          {ctc && (
            <div className="rg-date">
              <p className="eyebrow" style={{ color: 'var(--muted)' }}>Clear to close</p>
              <b>{day(ctc)}{ctcDays !== null && <> · {ctcDays} days</>}</b>
            </div>
          )}
          <div className="rg-date is-hard">
            <p className="eyebrow" style={{ color: 'var(--muted)' }}>Contract close</p>
            <b>{day(deal.closeDate)}{days !== null && <> · {days} days</>}</b>
          </div>
        </div>
      </section>

      <nav className="rg-tabs">
        <button onClick={() => setTop('today')} aria-current={top === 'today'}>{t('cc.today')}</button>
        <button onClick={() => setTop('funding')} aria-current={top === 'funding'}>{t('cc.funding')}</button>
        <button onClick={() => setTop('debt')} aria-current={top === 'debt'}>{t('cc.debt')}</button>
        <button onClick={() => setTop('file')} aria-current={top === 'file'}>{t('cc.file')}</button>
      </nav>

      {/*
        * One banner, above every tab, when the workbook's own checks disagree
        * with what the app pulled.
        *
        * The failing checks were three screens down inside a fold. A number you
        * cannot trust is more urgent than any number you can, so it goes first
        * and it says plainly not to act on a single tile.
        */}
      {conflicts.length > 0 && (
        <div className="rg-alert" role="status">
          <Icon name="alert" size={20} />
          <div>
            <h3>
              {t('cc.conflict')}
              <span className="rg-tag">{conflicts.length} to resolve</span>
            </h3>
            <p>{conflicts.slice(0, 3).map((c) => c.name).join(' · ')}
              {conflicts.length > 3 && ` · and ${conflicts.length - 3} more`}
              . Open The file to see the source and date behind each number.</p>
          </div>
        </div>
      )}

      {top === 'today' && (
        <>
          <div className="rg-cols">
            <section className="rg-card">
              <p className="eyebrow" style={{ color: 'var(--warn)' }}>Critical pair · today</p>
              <h2>{blocker ?? 'Protect the deal and unblock the loan — in parallel.'}</h2>
              {blockerTask?.next_action && (
                <p className="rg-lede"><strong>{blockerTask.next_action}</strong></p>
              )}
              {proofs.length > 0 && (
                <p className="rg-foot"><Icon name="check" size={14} /> {proofsDone} of {proofs.length} proofs recorded</p>
              )}

              {/*
                * The owner's own checklist, on the front page.
                *
                * It lived behind the Admin tab, which is where it was least
                * likely to be seen on the day it mattered — and its own source
                * calls it "the list the owner actually works from".
                */}
              <div className="rg-lanes">
                {laneItems.map((l) => (
                  <div className="rg-lane" key={l.list}>
                    <div className="rg-lane-ic"><Icon name={l.list === 'Credit' ? 'chart' : l.list === 'Data' ? 'info' : 'home'} size={18} /></div>
                    <h3>{l.list}</h3>
                    <p>{l.items.filter((i) => !i.done).length} still to prove</p>
                    {l.items.map((i) => (
                      <button
                        key={i.id}
                        type="button"
                        className="rg-proof"
                        aria-pressed={i.done}
                        onClick={() => void toggleProof(i)}
                        title={i.how ?? i.detail ?? undefined}
                      >
                        <i>{i.done && <Icon name="check" size={12} />}</i>
                        <span>{i.title}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </section>

            <section className="rg-card">
              <p className="eyebrow" style={{ color: 'var(--warn)' }}>Funding reality</p>
              <h2>{deal.refi && deal.refi.payoff !== null && deal.refi.landNeed !== null ? 'Not proven yet' : 'Needs a value'}</h2>
              {deal.refi && (
                <table className="rg-ledger">
                  <tbody>
                    <tr><td>Refinance request</td><td>{money(deal.refi.sought)}</td></tr>
                    <tr><td>Land balance after deposit</td><td>− {money(deal.refi.landNeed)}</td></tr>
                    <tr><td>Recorded card payoff</td><td>− {money(deal.refi.payoff)}</td></tr>
                    <tr className="rg-total">
                      <td>Pre-cost cushion</td>
                      <td>{money(deal.refi.sought - (deal.refi.landNeed ?? 0) - (deal.refi.payoff ?? 0))}</td>
                    </tr>
                  </tbody>
                </table>
              )}
              <div className="rg-note">
                <b>Still missing from the math</b>
                <p>
                  Closing costs, prepaids, lender reserves and any protected reserve.
                  {deal.refi?.debtScope === 'floor' && ' No non-card debt has been recorded, so the payoff above is a floor.'}
                </p>
              </div>
              <p className="rg-foot">
                <button type="button" className="btn-s" onClick={() => setTop('funding')}>See both LTV scenarios</button>
              </p>
            </section>
          </div>

          <div className="rg-tiles">
            <div className={`rg-tile ${deal.refi?.debtScope === 'floor' ? 'warn' : ''}`}>
              <div className="rg-tile-ic"><Icon name="chart" size={18} /></div>
              <p className="rg-tile-k">Everything recorded</p>
              <p className="rg-tile-v">{deal.refi ? money(deal.refi.payoff) : '—'}</p>
              <p className="rg-tile-s">
                {deal.refi?.debtScope === 'floor' ? 'still a floor — non-card debt not recorded'
                  : deal.refi?.debtScope === 'cards' ? 'cards only'
                  : 'cards and everything else'}
              </p>
            </div>
            <div className="rg-tile">
              <div className="rg-tile-ic"><Icon name="key" size={18} /></div>
              <p className="rg-tile-k">Pre-cost cushion</p>
              <p className="rg-tile-v">
                {deal.refi ? money(deal.refi.sought - (deal.refi.landNeed ?? 0) - (deal.refi.payoff ?? 0)) : '—'}
              </p>
              <p className="rg-tile-s">not true cash available</p>
            </div>
            <div className="rg-tile warn">
              <div className="rg-tile-ic"><Icon name="home" size={18} /></div>
              <p className="rg-tile-k">Appraisal needed</p>
              <p className="rg-tile-v">{deal.refi ? money(Math.round(deal.refi.sought / 0.8)) : '—'}</p>
              <p className="rg-tile-s">
                {deal.refi ? <>at 80% · {money(Math.round(deal.refi.sought / 0.75))} at 75%</> : 'needs a loan amount'}
              </p>
            </div>
            <div className={`rg-tile ${deal.credit?.mortgageMiddle?.score === null ? 'bad' : ''}`}>
              <div className="rg-tile-ic"><Icon name="shield" size={18} /></div>
              <p className="rg-tile-k">Lender score</p>
              <p className="rg-tile-v">{deal.credit?.mortgageMiddle?.score ?? 'Unknown'}</p>
              <p className="rg-tile-s">
                {creditMid !== null ? <>consumer range {creditLow}–{creditHigh} — not the lender&rsquo;s model</> : 'never pulled'}
                {workbookScore !== null && workbookScore !== creditMid && (
                  <span className="cc-was"> workbook says {workbookScore}</span>
                )}
              </p>
            </div>
            <div className="rg-tile bad">
              <div className="rg-tile-ic"><Icon name="alert" size={18} /></div>
              <p className="rg-tile-k">Invisible to credit</p>
              <p className="rg-tile-v">
                {deal.refi?.offBureauTotal && deal.refi.payoff
                  ? `${Math.round((deal.refi.offBureauTotal / deal.refi.payoff) * 100)}%`
                  : '—'}
              </p>
              <p className="rg-tile-s">
                {deal.refi?.offBureauTotal ? <>{money(deal.refi.offBureauTotal)} on no credit report</> : 'nothing recorded off-bureau'}
              </p>
            </div>
            <div className={`rg-tile ${deal.utilisation && deal.utilisation.ratio > 0.3 ? 'warn' : ''}`}>
              <div className="rg-tile-ic"><Icon name="clock" size={18} /></div>
              <p className="rg-tile-k">Reported utilisation</p>
              <p className="rg-tile-v">{deal.utilisation ? percent(deal.utilisation.ratio) : '—'}</p>
              <p className="rg-tile-s">
                {deal.utilisation
                  ? <>{deal.utilisation.bureau} · {day(deal.utilisation.pulledOn)} — money must post before a statement closes</>
                  : 'no report pulled'}
              </p>
            </div>
          </div>

          {/*
            * The critical path, in the order that changes the decision.
            *
            * Same rows as the table on The file, presented as one open item at
            * a time with the why, the how and the evidence it needs — because
            * a five-column table answers "what is outstanding" and never
            * answers "what do I actually do".
            */}
          <section className="rg-card">
            <p className="eyebrow" style={{ color: 'var(--brand)' }}>Then do this</p>
            <h2>In the order that changes the decision</h2>
            <p className="rg-lede">Open an item for the why, the how, and the evidence it needs.</p>
            <HideDone id="rg-tasks" label="Outstanding" hidden={deal.tasks.length - visibleTasks.length} on={hideDoneTasks} set={setHideDoneTasks} />
            <div className="rg-steps">
              {taskPage.shown.map((t) => {
                const open = openRows.has(t.ref)
                return (
                  <div className={`rg-step ${t.overdue ? 'late' : ''}`} key={t.ref}>
                    <button
                      type="button"
                      className="rg-step-head"
                      aria-expanded={open}
                      onClick={() => setOpenRows((prev) => {
                        const next = new Set(prev)
                        if (next.has(t.ref)) next.delete(t.ref)
                        else next.add(t.ref)
                        return next
                      })}
                    >
                      <span className="rg-when">
                        {t.due_on ? day(t.due_on) : 'no date'}
                        {t.overdue && ' · late'}
                      </span>
                      <span className="rg-step-ic"><Icon name={t.overdue ? 'alert' : 'check'} size={18} /></span>
                      <span>
                        <span className="rg-step-t">{t.task}</span>
                        <span className="rg-step-d">{t.next_action ?? t.workstream ?? '—'}</span>
                      </span>
                      <Icon name="chevron" size={16} />
                    </button>
                    {open && (
                      <div className="rg-step-body">
                        <div>
                          <h4 className="eyebrow" style={{ color: 'var(--brand)' }}>Why it matters</h4>
                          <p>{t.risk_if_late ?? 'No consequence recorded for this row.'}</p>
                          {t.evidence && (
                            <>
                              <h4 className="eyebrow" style={{ color: 'var(--brand)' }}>Evidence required</h4>
                              <p className="rg-ev">{t.evidence}</p>
                            </>
                          )}
                        </div>
                        <div>
                          <h4 className="eyebrow" style={{ color: 'var(--brand)' }}>How to clear it</h4>
                          <ol>
                            {t.next_action && <li>{t.next_action}</li>}
                            {t.dependency && <li>Waiting on {t.dependency}.</li>}
                            <li>Owner: {t.owner ?? 'nobody named'}. Due {day(t.due_on)}.</li>
                          </ol>
                          {!done(t.status) && (
                            <button type="button" className="btn-s btn-go" onClick={() => void act(`${t.ref} done`)}>Mark done</button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            <Pager p={taskPage} />
          </section>
        </>
      )}

      {top === 'funding' && (
        <div className="rg-cols even">
          <section className="rg-card">
            <p className="eyebrow" style={{ color: 'var(--brand)' }}>Sources and uses</p>
            <h2>The entire deal — not just the loan amount</h2>
            {deal.refi ? (
              <table className="rg-ledger">
                <tbody>
                  <tr>
                    <td><span className="rg-tagset">Gross refinance request
                      <span className={`rg-t ${deal.refi.soughtSetByHand ? 'est' : 'known'}`}>{deal.refi.soughtSetByHand ? 'set by hand' : 'known'}</span>
                    </span></td>
                    <td>{money(deal.refi.sought)}</td>
                  </tr>
                  <tr>
                    <td><span className="rg-tagset">Land balance after deposit <span className="rg-t known">known</span></span></td>
                    <td>− {money(deal.refi.landNeed)}</td>
                  </tr>
                  <tr>
                    <td><span className="rg-tagset">Recorded card payoff
                      <span className={`rg-t ${deal.refi.debtScope === 'floor' ? 'est' : 'known'}`}>{deal.refi.debtScope === 'floor' ? 'a floor' : 'pulled'}</span>
                    </span></td>
                    <td>− {money(deal.refi.payoff)}</td>
                  </tr>
                  <tr className="rg-bad">
                    <td><span className="rg-tagset">Refinance costs + prepaids <span className="rg-t missing">missing</span></span></td>
                    <td>Missing</td>
                  </tr>
                  <tr className="rg-bad">
                    <td><span className="rg-tagset">Purchase closing costs <span className="rg-t missing">missing</span></span></td>
                    <td>Missing</td>
                  </tr>
                  <tr className="rg-bad">
                    <td><span className="rg-tagset">Taxes and other liabilities <span className="rg-t missing">missing</span></span></td>
                    <td>{deal.refi.nonCardTotal ? money(deal.refi.nonCardTotal) : 'Missing'}</td>
                  </tr>
                  <tr className="rg-total">
                    <td>Current result</td>
                    <td>Funding not proven</td>
                  </tr>
                </tbody>
              </table>
            ) : <p className="rg-lede">The workbook has no financing structure yet.</p>}
          </section>

          <section className="rg-card">
            <p className="eyebrow" style={{ color: 'var(--warn)' }}>Decision lock</p>
            <h2>Occupancy changes the ceiling</h2>
            <p className="rg-lede">
              Planning scenarios only. The classification must match the property's real intended
              use and be confirmed by the lender.
            </p>
            {deal.refi && (
              <div className="rg-scen">
                <div className="rg-scen-box">
                  <b>80<small>%</small></b>
                  <h4>One-unit primary</h4>
                  <dl>
                    <div><dt>Appraisal for {money(deal.refi.sought)}</dt><dd>{money(Math.round(deal.refi.sought / 0.8))}</dd></div>
                    <div><dt>Capacity at {money(deal.refi.collateral)}</dt><dd>{money(Math.round(deal.refi.collateral * 0.8))}</dd></div>
                  </dl>
                </div>
                <div className="rg-scen-box">
                  <b>75<small>%</small></b>
                  <h4>Second / investment</h4>
                  <dl>
                    <div><dt>Appraisal for {money(deal.refi.sought)}</dt><dd>{money(Math.round(deal.refi.sought / 0.75))}</dd></div>
                    <div><dt>Capacity at {money(deal.refi.collateral)}</dt><dd>{money(deal.refi.maxAt75)}</dd></div>
                  </dl>
                </div>
              </div>
            )}
            <div className="rg-rows">
              {locks.map((g) => (
                <div className="rg-row" key={g.ref}>
                  <span>{g.name}</span>
                  <span className="rg-t missing">unverified</span>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {top === 'debt' && (
        <div className="rg-cols even">
          <section className="rg-card">
            <p className="eyebrow" style={{ color: 'var(--danger)' }}>Hold the release</p>
            <h2>Proposed — not authorized</h2>
            <p className="rg-lede">
              A paydown moves reported utilisation before a statement closes. It should not move
              until the lender confirms what the file actually needs.
            </p>
            {deal.refi?.offBureauTotal ? (
              <div className="rg-note">
                <b>Do not pay the largest balance by instinct.</b>
                <p>
                  {money(deal.refi.offBureauTotal)} of what is owed is absent from the reports on
                  file. Paying it reduces real debt and real interest, and cannot move the
                  lender-used score.
                </p>
              </div>
            ) : null}
            {deal.utilisation && (
              <p className="rg-foot">
                To 30% costs {money(deal.utilisation.toThirty)} · to 10% costs {money(deal.utilisation.toTen)}
              </p>
            )}
          </section>

          <section className="rg-card">
            <p className="eyebrow" style={{ color: 'var(--brand)' }}>Credit snapshot</p>
            <h2>Reporting debt versus real debt</h2>
            <div className="rg-tiles" style={{ marginTop: 16, marginBottom: 0 }}>
              <div className="rg-tile">
                <p className="rg-tile-k">Visible on report</p>
                <p className="rg-tile-v">{deal.utilisation ? money(deal.utilisation.balance) : '—'}</p>
              </div>
              <div className="rg-tile">
                <p className="rg-tile-k">Not on the reports</p>
                <p className="rg-tile-v">{deal.refi?.offBureauTotal ? money(deal.refi.offBureauTotal) : '—'}</p>
              </div>
              <div className="rg-tile">
                <p className="rg-tile-k">Recorded total</p>
                <p className="rg-tile-v">{deal.refi ? money(deal.refi.payoff) : '—'}</p>
              </div>
            </div>
            {(deal.utilisation?.bureaus ?? []).map((b) => (
              <div className="rg-meter" key={b.bureau}>
                <div className="rg-meter-h">
                  <span>{b.bureau} utilisation</span>
                  <b>{b.ratio === null ? '—' : `${(b.ratio * 100).toFixed(1)}%`}</b>
                </div>
                <div className="rg-meter-bar">
                  <i
                    className={b.ratio !== null && b.ratio < 0.3 ? 'ok' : b.ratio !== null && b.ratio < 0.5 ? 'warn' : ''}
                    style={{ width: `${pct(b.ratio) ?? 0}%` }}
                  />
                </div>
              </div>
            ))}
            <p className="rg-foot">
              <Icon name="info" size={14} />
              Card numbers and payment routing stay in the protected file.
            </p>
          </section>
        </div>
      )}

      {top === 'file' && (
        <>
          <div className="rg-cols even">
            <section className="rg-card">
              <p className="eyebrow" style={{ color: 'var(--brand)' }}>Decision locks</p>
              <h2>{locks.length} answers before the plan can call itself ready</h2>
              <div className="rg-rows">
                {deal.gates.map((g) => (
                  <button
                    type="button"
                    key={g.ref}
                    className={`rg-lock ${done(g.status) ? 'done' : ''}`}
                    onClick={() => void act(`tell me about ${g.ref}`)}
                  >
                    <span className="rg-lock-ic"><Icon name={done(g.status) ? 'check' : 'key'} size={16} /></span>
                    <span>
                      <b>{g.name}</b>
                      <small>Owner: {g.owner ?? 'nobody named'}{g.due_on ? ` · ${day(g.due_on)}` : ''}</small>
                    </span>
                  </button>
                ))}
              </div>
            </section>

            {/*
              * Provenance. Every number gets a source and a date, or it is
              * marked missing — which is the difference between a dashboard and
              * a claim.
              */}
            <section className="rg-card">
              <p className="eyebrow" style={{ color: 'var(--warn)' }}>Data provenance</p>
              <h2>Every number needs a source and a date</h2>
              <div className="rg-rows">
                <div className="rg-row">
                  <span><b>Card balances</b><br /><span className="rg-row-src">42payments · Plaid</span></span>
                  <span className={`rg-t ${deal.refi?.cardsAsOf ? 'known' : 'missing'}`}>
                    {deal.refi?.cardsAsOf ? `pulled ${day(deal.refi.cardsAsOf)}` : 'never pulled'}
                  </span>
                </div>
                <div className="rg-row">
                  <span><b>Utilisation</b><br /><span className="rg-row-src">Credit reports</span></span>
                  <span className={`rg-t ${deal.utilisation ? 'known' : 'missing'}`}>
                    {deal.utilisation ? `${deal.utilisation.bureau} · ${day(deal.utilisation.pulledOn)}` : 'missing'}
                  </span>
                </div>
                <div className="rg-row">
                  <span><b>Mortgage score</b><br /><span className="rg-row-src">Lender tri-merge pull</span></span>
                  <span className={`rg-t ${deal.credit?.mortgageMiddle?.score ? 'known' : 'missing'}`}>
                    {deal.credit?.mortgageMiddle?.score ?? 'missing'}
                  </span>
                </div>
                <div className="rg-row">
                  <span><b>Tax and non-card liabilities</b><br /><span className="rg-row-src">CPA · transcripts</span></span>
                  <span className={`rg-t ${deal.refi?.nonCardTotal ? 'known' : 'missing'}`}>
                    {deal.refi?.nonCardTotal ? money(deal.refi.nonCardTotal) : 'missing'}
                  </span>
                </div>
                <div className="rg-row">
                  <span><b>Workbook snapshot</b><br /><span className="rg-row-src">{deal.snapshot?.sourcePath ?? 'imported'}</span></span>
                  <span className={`rg-t ${deal.snapshot?.stale ? 'est' : 'known'}`}>
                    {deal.snapshot ? new Date(deal.snapshot.createdAt).toLocaleDateString() : '—'}
                  </span>
                </div>
              </div>
            </section>
          </div>

          {control.length > 0 && (
            <section className="rg-card" style={{ marginBottom: 20 }}>
              <p className="eyebrow" style={{ color: 'var(--brand)' }}>Control metrics</p>
              <h2>{control.length} tracked</h2>
              <div className="rg-rows">
                {control.map((c) => (
                  <div className="rg-row" key={c.key}>
                    <span>{c.label}</span>
                    <span className="rg-num">{show(c)}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/*
            * The whole work surface, unchanged: the critical path table, the
            * gates, the checks, the risks, the people, the tickets and the
            * workbook sheets. The redesign reorganised the front page; it did
            * not delete the record behind it.
            */}
          <section className="cc-tabs">
            <nav className="cc-tabbar">
              <button onClick={() => setTab('path')} aria-current={tab === 'path'}>
                Critical path <em className="cc-pill cc-pill-quiet">{visibleTasks.length}</em>
                {deal.counts.overdue > 0 && <em className="cc-pill">{deal.counts.overdue} overdue</em>}
              </button>
              <button onClick={() => setTab('gates')} aria-current={tab === 'gates'}>Decision gates</button>
              <button onClick={() => setTab('checks')} aria-current={tab === 'checks'}>
                Checks {deal.counts.checksFailing > 0 && <em className="cc-pill">{deal.counts.checksFailing}</em>}
              </button>
              <button onClick={() => setTab('risks')} aria-current={tab === 'risks'}>
                Risks {deal.counts.risksOpenHigh > 0 && <em className="cc-pill">{deal.counts.risksOpenHigh} high</em>}
              </button>
              {/* People and Tickets are not "work items" like the four before them,
                  and sitting unlabelled among them is why the parties list could not
                  be found. A rule separates them, and each says what it holds. */}
              <span className="cc-tabsep" aria-hidden="true" />
              <button onClick={() => setTab('parties')} aria-current={tab === 'parties'}>
                People <em className="cc-pill cc-pill-quiet">{partyCount ?? '·'}</em>
              </button>
              <button onClick={() => setTab('tickets')} aria-current={tab === 'tickets'}>Tickets</button>
              <button onClick={() => setTab('sheets')} aria-current={tab === 'sheets'}>
                Workbook <em className="cc-pill cc-pill-quiet">{deal.sheets?.length ?? 0}</em>
              </button>
            </nav>

            {tab === 'path' && (
              <>
              <HideDone id="f-tasks" label="Outstanding" hidden={deal.tasks.length - visibleTasks.length} on={hideDoneTasks} set={setHideDoneTasks} />
              <table className="cc-table">
                <thead><tr><th>Task</th><th>Owner</th><th>Due</th><th>Status</th><th>Next action</th></tr></thead>
                <tbody>
                  {taskPage.shown.flatMap((t) => [
                    <tr key={t.ref} className={t.overdue ? 'overdue' : ''}>
                      {/* The id joins the task rather than owning a column of its
                          own: a 4-character cell next to a 60-character one is what
                          made the left of this table empty and the right crowded. */}
                      <td className="cc-task">
                        <strong><span className="cc-ref">{t.ref}</span>{t.task}</strong>
                        <span className="cc-sub">{t.workstream}</span>
                      </td>
                      <td>{t.owner ?? '—'}</td>
                      <td>{day(t.due_on)}{t.overdue && <span className="cc-flag">overdue</span>}</td>
                      <td>
                        {t.status ?? '—'}
                        {t.overridden?.status !== undefined && (
                          <span className="cc-ovr" title={`The workbook says ${t.overridden.status ?? 'nothing'}`}>set here</span>
                        )}
                      </td>
                      <td className="cc-sub">
                        {t.next_action ?? '—'}
                        <span className="cc-actions">
                          {!done(t.status) && (
                            <button type="button" className="btn-s btn-go" onClick={() => void act(`${t.ref} done`)}>Mark done</button>
                          )}
                          <button
                            type="button"
                            className="btn-s"
                            aria-expanded={openRows.has(t.ref)}
                            onClick={() => setOpenRows((prev) => {
                              const next = new Set(prev)
                              if (next.has(t.ref)) next.delete(t.ref)
                              else next.add(t.ref)
                              return next
                            })}
                          >
                            {openRows.has(t.ref) ? 'Hide' : 'Details'}
                          </button>
                        </span>
                      </td>
                    </tr>,
                    openRows.has(t.ref) && (
                      <tr key={`${t.ref}-open`} className="cc-open">
                        <td colSpan={5}>
                          <dl className="cc-detail">
                            {([
                              ['Depends on', t.dependency],
                              ['Evidence', t.evidence],
                              ['Next action', t.next_action],
                              ['Risk if late', t.risk_if_late],
                              ['Owner', t.owner],
                              ['Due', day(t.due_on)],
                            ] as Array<[string, string | null]>)
                              .filter(([, v]) => v && v !== '—')
                              .map(([k, v]) => (
                                <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
                              ))}
                          </dl>
                        </td>
                      </tr>
                    ),
                  ]).flat().filter(Boolean)}
                </tbody>
              </table>
              <Pager p={taskPage} />
              </>
            )}

            {tab === 'gates' && (
              <table className="cc-table">
                <thead><tr><th>Gate</th><th>Deadline</th><th>Pass condition</th><th>Owner</th><th>Status</th><th>If not passed</th></tr></thead>
                <tbody>
                  {deal.gates.map((g) => (
                    <tr key={g.ref} className={g.overdue ? 'overdue' : ''}>
                      <td><strong>{g.name}</strong></td>
                      <td>{day(g.due_on)}{g.daysUntil !== null && <span className="cc-sub">{g.daysUntil < 0 ? `${-g.daysUntil}d late` : `in ${g.daysUntil}d`}</span>}</td>
                      <td className="cc-sub">{g.pass_condition ?? '—'}</td>
                      <td>{g.owner ?? '—'}</td>
                      <td>
                        {g.status ?? '—'}
                        {g.overridden?.status !== undefined && (
                          <span className="cc-ovr" title={`The workbook says ${g.overridden.status ?? 'nothing'}`}>set here</span>
                        )}
                      </td>
                      <td className="cc-sub">
                        {g.if_not_passed ?? '—'}
                        <span className="cc-actions">
                          {!done(g.status) && (
                            <button type="button" className="btn-s btn-go" onClick={() => void act(`${g.ref} passed`)}>Mark passed</button>
                          )}
                          <button type="button" className="btn-s" onClick={() => void act(`tell me about ${g.ref}`)}>Details</button>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {tab === 'checks' && (
              <>
              <HideDone id="f-checks" label="Failing" hidden={deal.checks.length - visibleChecks.length} on={hidePassChecks} set={setHidePassChecks} />
              <table className="cc-table">
                <thead><tr><th>Check</th><th>Status</th><th>Where to fix</th></tr></thead>
                <tbody>
                  {checkPage.shown.map((c) => (
                    <tr key={c.ref} className={c.status === 'PASS' ? '' : 'overdue'}>
                      <td>{c.ref} · {c.name}</td>
                      <td><span className={`cc-badge ${c.status === 'PASS' ? 'ok' : 'bad'}`}>{c.status}</span></td>
                      {/* NEEDS INPUT used to be a dead end. It now names the cell to
                          fix AND the command that records it here. */}
                      <td className="cc-sub">
                        {c.where_to_fix ?? '—'}
                        {c.status !== 'PASS' && (
                          <span className="cc-actions">
                            <button type="button" className="btn-s" onClick={() => void act(`tell me about ${c.ref}`)}>Details</button>
                            <button type="button" className="btn-s" onClick={() => void act(`ticket ${c.name}`)}>Make a ticket</button>
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pager p={checkPage} />
              </>
            )}

            {tab === 'parties' && <Parties />}

            {tab === 'tickets' && <Tickets />}

            {tab === 'sheets' && <Sheets sheets={deal.sheets ?? []} />}

            {tab === 'risks' && (
              <>
              <HideDone id="f-risks" label="Open" hidden={deal.risks.length - visibleRisks.length} on={hideClosedRisks} set={setHideClosedRisks} />
              <table className="cc-table">
                <thead><tr><th>ID</th><th>Risk</th><th>Impact</th><th>Likelihood</th><th>Mitigation</th><th>Status</th></tr></thead>
                <tbody>
                  {riskPage.shown.map((r) => (
                    <tr key={r.ref} className={r.impact === 'High' && r.status !== 'Closed' ? 'overdue' : ''}>
                      <td>{r.ref}</td>
                      <td><strong>{r.risk}</strong></td>
                      <td>{r.impact ?? '—'}</td>
                      <td>{r.likelihood ?? '—'}</td>
                      <td className="cc-sub">{r.mitigation ?? '—'}</td>
                      <td>
                        {r.status ?? '—'}
                        <span className="cc-actions">
                          {!done(r.status) && (
                            <button type="button" className="btn-s" onClick={() => void act(`${r.ref} closed`)}>Close</button>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pager p={riskPage} />
              </>
            )}
          </section>

          {deal.statements.immediate_gate && (
            <Collapsible id="gate-rule" title="Immediate gate" count="do not do these">
              <section className="cc-gate-rule">
                <p>{deal.statements.immediate_gate}</p>
              </section>
            </Collapsible>
          )}
        </>
      )}

      {/* The command line stays on the page rather than behind a button: acting
          on what you just read should not be a second journey. `/` focuses it,
          and so does Ask The Ridge in the header. */}
      <VoiceOfGod onChanged={() => void load()} />

      <p className="cc-foot muted">
        Snapshot of the workbook, imported {new Date(deal.snapshot!.createdAt).toLocaleString()}.
        {deal.snapshot!.sourceModifiedAt && ` Workbook last edited ${new Date(deal.snapshot!.sourceModifiedAt).toLocaleDateString()}.`}
        {deal.snapshot!.stale && ' The workbook has not been touched in over a week — re-import if it has moved on.'}
        {' '}The spreadsheet remains the model of record.
      </p>
    </div>
  )
}
